import { useState } from "react";
import { Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const KEY = "vts_email_optin_v1";

/* 📧 Connect-time email capture — HARD GATE (owner choice, June 2026).
   Deriv's new API never exposes the user's email, so after connecting the
   user MUST save one to continue: no Later button, no X, no ESC/outside
   dismiss. Signing out is the only other way off this screen. */
const EmailCaptureDialog = ({ open, onClose, accountId = "" }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

  const save = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await api.post("/marketing/email", { email: email.trim(), source: "deriv_connect", account_id: accountId });
      try { localStorage.setItem(KEY, email.trim().toLowerCase()); } catch { /* noop */ }
      toast.success("📧 You're set", { description: "Account emails (welcome, funding help, inactivity) go there. We never ask for your password by email." });
      onClose();
    } catch {
      toast.error("Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* 🔒 hard gate — only save closes it */ }}>
      <DialogContent
        data-testid="email-capture-dialog"
        className="max-w-sm bg-[#0B0E14] border-white/10 [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white text-base">
            <Mail className="w-4 h-4 text-[#089981]" /> One last step — your email
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] font-mono text-white/55 leading-relaxed">
          Required to continue. Deriv doesn't share your email with apps, so drop it here — we
          use it for account emails only: welcome, funding help if your real account sits empty,
          and a nudge if you've been away. No spam, and we never ask for your password by email.
        </p>
        <input
          data-testid="email-capture-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); }}
          placeholder="you@example.com"
          className="w-full rounded-lg border border-white/15 bg-[#131722] px-3 py-2.5 text-sm text-white placeholder:text-white/25 outline-none focus:border-[#089981]/60"
        />
        <div>
          <button
            data-testid="email-capture-save"
            onClick={save}
            disabled={!valid || busy}
            className="w-full rounded-lg bg-[#089981] px-3 py-2.5 text-sm font-black text-white disabled:opacity-30 hover:bg-[#0aa98f] transition-colors"
          >
            {busy ? "Saving…" : "Save email & continue"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailCaptureDialog;
